import { TenantPlusModalOpenTrigger } from '@/helpers/TenantPlus/TenantPlusModalOpenTriggers';

export enum TenantPlusStandardBenefit {
  CONTACT_INFO = 'contactInfo',
  EXCLUSIVE_LISTINGS = 'exclusiveListings',
  LISTING_DEMAND = 'listingDemand',
  FREE_DEBT_EXTRACT = 'freeDebtExtract',
  MOVING_CLEANING_DISCOUNT = 'movingCleaningDiscount',
}

export const TENANT_PLUS_STANDARD_BENEFIT_PRIORITY: TenantPlusStandardBenefit[] = [
  TenantPlusStandardBenefit.CONTACT_INFO,
  TenantPlusStandardBenefit.EXCLUSIVE_LISTINGS,
  TenantPlusStandardBenefit.LISTING_DEMAND,
  TenantPlusStandardBenefit.FREE_DEBT_EXTRACT,
  TenantPlusStandardBenefit.MOVING_CLEANING_DISCOUNT,
];

const SIGN_UP_REQUIRED_MODAL_BENEFIT_KEY: Record<TenantPlusStandardBenefit, string> = {
  [TenantPlusStandardBenefit.CONTACT_INFO]: 'tenantPlus.signUpRequiredModal.benefit1',
  [TenantPlusStandardBenefit.EXCLUSIVE_LISTINGS]: 'tenantPlus.signUpRequiredModal.benefit2',
  [TenantPlusStandardBenefit.LISTING_DEMAND]: 'tenantPlus.signUpRequiredModal.benefit3',
  [TenantPlusStandardBenefit.FREE_DEBT_EXTRACT]: 'tenantPlus.signUpRequiredModal.benefit4',
  [TenantPlusStandardBenefit.MOVING_CLEANING_DISCOUNT]: 'tenantPlus.signUpRequiredModal.benefit5',
};

const CONTACT_BLOCKED_MODAL_OPEN_TRIGGERS: TenantPlusModalOpenTrigger[] = [
  TenantPlusModalOpenTrigger.CONTACT_FORM,
  TenantPlusModalOpenTrigger.PHONE_NUMBER,
  TenantPlusModalOpenTrigger.LISTER_PHONE_NUMBER,
  TenantPlusModalOpenTrigger.APPOINTMENT_VIEWING,
  TenantPlusModalOpenTrigger.REQUEST_ADDRESS,
  TenantPlusModalOpenTrigger.DESCRIPTION_CONTACT,
];

const contactBlockedBenefitByTrigger = Object.fromEntries(
  CONTACT_BLOCKED_MODAL_OPEN_TRIGGERS.map((trigger) => [
    trigger,
    TenantPlusStandardBenefit.CONTACT_INFO,
  ]),
) as Partial<Record<TenantPlusModalOpenTrigger, TenantPlusStandardBenefit>>;

const BLOCKED_STANDARD_BENEFIT_BY_TRIGGER: Partial<
  Record<TenantPlusModalOpenTrigger, TenantPlusStandardBenefit>
> = {
  ...contactBlockedBenefitByTrigger,
  [TenantPlusModalOpenTrigger.LISTING_STATS]: TenantPlusStandardBenefit.LISTING_DEMAND,
};

const CUSTOM_BLOCKED_BENEFIT_KEY_BY_TRIGGER: Partial<Record<TenantPlusModalOpenTrigger, string>> = {
  [TenantPlusModalOpenTrigger.MORE_IMAGES]: 'tenantPlus.exclusiveImagesModal.benefit',
};

const getRemainingStandardBenefits = (
  blockedStandardBenefit: TenantPlusStandardBenefit | undefined,
): TenantPlusStandardBenefit[] =>
  TENANT_PLUS_STANDARD_BENEFIT_PRIORITY.filter((benefit) => benefit !== blockedStandardBenefit);

/**
 * Returns exactly five i18n keys for TenantPlus upsell modals.
 * First key describes the blocked action; remaining keys follow standard benefit priority.
 */
export const getTenantPlusModalBenefitTranslationKeys = (
  modalOpenTrigger: TenantPlusModalOpenTrigger,
): string[] => {
  const blockedStandardBenefit = BLOCKED_STANDARD_BENEFIT_BY_TRIGGER[modalOpenTrigger];
  const customBlockedBenefitKey = CUSTOM_BLOCKED_BENEFIT_KEY_BY_TRIGGER[modalOpenTrigger];

  if (!blockedStandardBenefit && !customBlockedBenefitKey) {
    return TENANT_PLUS_STANDARD_BENEFIT_PRIORITY.map(
      (benefit) => SIGN_UP_REQUIRED_MODAL_BENEFIT_KEY[benefit],
    );
  }

  const firstBenefitKey = blockedStandardBenefit
    ? SIGN_UP_REQUIRED_MODAL_BENEFIT_KEY[blockedStandardBenefit]
    : customBlockedBenefitKey;

  if (!firstBenefitKey) {
    return TENANT_PLUS_STANDARD_BENEFIT_PRIORITY.map(
      (benefit) => SIGN_UP_REQUIRED_MODAL_BENEFIT_KEY[benefit],
    );
  }

  const remainingBenefitKeys = getRemainingStandardBenefits(blockedStandardBenefit).map(
    (benefit) => SIGN_UP_REQUIRED_MODAL_BENEFIT_KEY[benefit],
  );

  return [firstBenefitKey, ...remainingBenefitKeys.slice(0, 4)];
};

export const shouldShowTenantPlusMovingDiscountCaption = (
  benefitTranslationKeys: string[],
): boolean => {
  return benefitTranslationKeys.includes(
    SIGN_UP_REQUIRED_MODAL_BENEFIT_KEY[TenantPlusStandardBenefit.MOVING_CLEANING_DISCOUNT],
  );
};
